import React, { useState, useEffect } from 'react';
import { templateAPI, znsAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../styles/SendBulkZNS.css';

function SendBulkZNS() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await templateAPI.getAll();
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      toast.error('Không thể tải danh sách template');
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    setResults(null);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    const fileExt = selectedFile.name.toLowerCase();
    const isValidType = allowedTypes.includes(selectedFile.type) || 
                       fileExt.endsWith('.xlsx') || 
                       fileExt.endsWith('.xls') || 
                       fileExt.endsWith('.csv');

    if (!isValidType) {
      toast.error('Chỉ chấp nhận file Excel (.xlsx, .xls) hoặc CSV');
      e.target.value = null;
      return;
    }

    // Validate file size (5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File quá lớn. Tối đa 5MB');
      e.target.value = null;
      return;
    }

    setFile(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedTemplate) {
      toast.error('Vui lòng chọn template trước');
      return;
    }

    try {
      const response = await znsAPI.downloadTemplateFile(selectedTemplate.id);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${selectedTemplate.id}_example.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('File mẫu đã được tải xuống');
    } catch (error) {
      toast.error('Không thể tải file mẫu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTemplate) {
      toast.error('Vui lòng chọn template');
      return;
    }

    if (!file) {
      toast.error('Vui lòng chọn file Excel/CSV');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_id', selectedTemplate.id);

      const response = await znsAPI.sendBulk(formData);

      if (response.data.success) {
        setResults(response.data.data);
        
        if (response.data.data.failed === 0) {
          toast.success(`Gửi thành công tất cả ${response.data.data.success} tin nhắn!`);
        } else {
          toast.warning(
            `Gửi xong! Thành công: ${response.data.data.success}, Thất bại: ${response.data.data.failed}`
          );
        }

        // Clear file input
        setFile(null);
        document.getElementById('file-input').value = null;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Lỗi khi gửi ZNS hàng loạt';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bulk-send-container">
      <div className="page-header">
        <h1>Gửi ZNS hàng loạt</h1>
        <p>Upload file Excel/CSV để gửi tin nhắn đến nhiều người</p>
      </div>

      <div className="bulk-send-content">
        <div className="upload-section">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Chọn template *</label>
              <select 
                onChange={handleTemplateChange} 
                disabled={uploading}
                required
              >
                <option value="">-- Chọn template --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.template_name} (ID: {template.template_id})
                  </option>
                ))}
              </select>
            </div>

            {selectedTemplate && (
              <>
                <div className="template-info-box">
                  <h4>Thông tin template</h4>
                  <div className="info-grid">
                    <div>
                      <span className="label">Các cột cần có trong file:</span>
                      <div className="params-required">
                        <span className="param-badge required">phone_number</span>
                        {JSON.parse(selectedTemplate.parameters || '[]').map((param, idx) => (
                          <span key={idx} className="param-badge">
                            {param.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="btn-secondary btn-download"
                  >
                    📥 Tải file mẫu Excel
                  </button>
                </div>

                <div className="form-group">
                  <label>Upload file Excel/CSV *</label>
                  <div className="file-upload-box">
                    <input
                      id="file-input"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      disabled={uploading}
                      required
                    />
                    {file && (
                      <div className="file-selected">
                        <span className="file-icon">📄</span>
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    )}
                  </div>
                  <small>
                    Định dạng: .xlsx, .xls, .csv | Tối đa: 5MB, 100 người/lần
                  </small>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary btn-upload"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="spinner"></span>
                        Đang gửi...
                      </>
                    ) : (
                      <>📤 Gửi hàng loạt</>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {results && (
          <div className="results-section">
            <h3>Kết quả gửi tin</h3>
            
            <div className="results-summary">
              <div className="stat-box total">
                <div className="stat-number">{results.total}</div>
                <div className="stat-label">Tổng số</div>
              </div>
              
              <div className="stat-box success">
                <div className="stat-number">{results.success}</div>
                <div className="stat-label">Thành công</div>
              </div>
              
              <div className="stat-box failed">
                <div className="stat-number">{results.failed}</div>
                <div className="stat-label">Thất bại</div>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div className="errors-list">
                <h4>Chi tiết lỗi ({results.errors.length})</h4>
                <div className="error-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Dòng</th>
                        <th>Số điện thoại</th>
                        <th>Lỗi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.errors.map((error, idx) => (
                        <tr key={idx}>
                          <td>{error.row}</td>
                          <td>{error.phone}</td>
                          <td className="error-message">{error.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="instructions-box">
        <h3>📖 Hướng dẫn sử dụng</h3>
        <ol>
          <li>Chọn template muốn gửi</li>
          <li>Click "Tải file mẫu Excel" để có file với format đúng</li>
          <li>Điền thông tin người nhận vào file Excel (mỗi người 1 dòng)</li>
          <li>Upload file lên và nhấn "Gửi hàng loạt"</li>
          <li>Chờ hệ thống gửi và xem kết quả</li>
        </ol>
        
        <div className="tips">
          <strong>💡 Lưu ý:</strong>
          <ul>
            <li>File phải có cột <code>phone_number</code> (hoặc <code>sdt</code>)</li>
            <li>Số điện thoại format: 0912345678 hoặc 84912345678</li>
            <li>Tối đa 100 người/lần gửi</li>
            <li>Hệ thống sẽ tự động delay giữa các tin để tránh spam</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SendBulkZNS;
